import { useQuery } from "@tanstack/react-query";

import type { CollectionJobsResponse } from "../types/admin";

async function fetchCollectionJobs(collectionId: number): Promise<CollectionJobsResponse> {
  const response = await fetch(`/api/collection/${collectionId}/jobs`, {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

interface UseCollectionJobsOptions {
  pollIntervalMs?: number;
}

function isJobSettled(job: CollectionJobsResponse["download"]): boolean {
  const resolved = job.completed + job.skipped + job.failed;
  return resolved >= job.total;
}

function shouldContinuePolling(jobs: CollectionJobsResponse | undefined): boolean {
  if (!jobs) {
    return true;
  }

  return !(
    isJobSettled(jobs.download) &&
    isJobSettled(jobs.convert) &&
    isJobSettled(jobs.extract) &&
    isJobSettled(jobs.index)
  );
}

export function useCollectionJobs(
  collectionId: number | null | undefined,
  options?: UseCollectionJobsOptions,
) {
  return useQuery({
    queryKey: ["collection-jobs", collectionId],
    queryFn: async () => fetchCollectionJobs(collectionId!),
    enabled: collectionId != null,
    refetchInterval: (query) => {
      const pollInterval = options?.pollIntervalMs ?? 2_000;
      const data = query.state.data as CollectionJobsResponse | undefined;
      return shouldContinuePolling(data) ? pollInterval : false;
    },
  });
}
